import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

type ThemeContextType = {
  theme: Theme;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_KEY = 'theme';
const THEME_USER_SET_KEY = 'theme-user-set';

// Function to get initial theme — site default is light.
const getInitialTheme = (): Theme => {
  if (typeof window === 'undefined') return 'light';

  try {
    const userSet = localStorage.getItem(THEME_USER_SET_KEY);
    const savedTheme = localStorage.getItem(THEME_KEY) as Theme | null;
    if (userSet === '1' && (savedTheme === 'light' || savedTheme === 'dark')) {
      return savedTheme;
    }
    return 'light';
  } catch {
    return 'light';
  }
};

// Function to apply theme to document immediately
const applyThemeToDocument = (theme: Theme) => {
  if (typeof window === 'undefined') return;

  const root = window.document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(theme);
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>('light');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Set initial theme immediately to prevent flash
    const initialTheme = getInitialTheme();
    setTheme(initialTheme);
    applyThemeToDocument(initialTheme);
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    // Apply theme to document and save to localStorage
    applyThemeToDocument(theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch (e) {
      // Handle localStorage errors gracefully
      console.warn('Could not save theme preference:', e);
    }
  }, [theme, mounted]);

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(THEME_USER_SET_KEY, '1');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // Prevent flash during SSR/initial render
  if (!mounted) {
    return <>{children}</>;
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    // Return a default theme during SSR
    return {
      theme: 'light' as Theme,
      toggleTheme: () => {},
    };
  }
  return context;
};
